import WidgetConfigForm from './WidgetConfigForm';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DIRECTUS_INSTANCE, fetchCurrentUser } from '../../api';

const WidgetDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workspaceId = localStorage.getItem('workspace_id');
  const isAgentScopedPath = location.pathname.startsWith('/agent/ai-agents');
  const aiAgentsBasePath = isAgentScopedPath ? '/agent/ai-agents' : '/ai-agents';

  const handleCancel = () => {
    navigate(`${aiAgentsBasePath}?tab=overview`);
  };

  return (
    <div className="min-h-full bg-transparent max-w-7xl mx-auto">
      <WidgetConfigForm
        workspaceId={workspaceId}
        configId={id}
        onSuccess={() => {}}
        onCancel={handleCancel}
      />
    </div>
  );
};

// Wrapper component for creating a new widget
const CreateWidgetForm = () => {
  const workspaceId = localStorage.getItem('workspace_id');
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingExistingWidget, setCheckingExistingWidget] = useState(true);
  const isAgentScopedPath = location.pathname.startsWith('/agent/ai-agents');
  const aiAgentsBasePath = isAgentScopedPath ? '/agent/ai-agents' : '/ai-agents';

  useEffect(() => {
    let cancelled = false;

    const ensureWidgetExistsAndOpen = async () => {
      try {
        const token = localStorage.getItem('directus_token');
        if (!token) {
          if (!cancelled) setCheckingExistingWidget(false);
          return;
        }

        const profile = await fetchCurrentUser(['first_name', 'linked_workspace.id', 'linked_workspace.name']) || {};

        let resolvedWorkspaceId = workspaceId || profile?.linked_workspace?.id || null;
        if (resolvedWorkspaceId) {
          localStorage.setItem('workspace_id', resolvedWorkspaceId);
        }

        if (!resolvedWorkspaceId) {
          if (!cancelled) setCheckingExistingWidget(false);
          return;
        }

        const widgetsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields: ['id'],
            limit: 1,
            sort: '-date_updated',
            filter: JSON.stringify({
              widget_linked_workspace: { _eq: resolvedWorkspaceId }
            })
          }
        });

        const existingWidgetId = widgetsResponse.data?.data?.[0]?.id;
        if (existingWidgetId) {
          navigate(`${aiAgentsBasePath}/${existingWidgetId}?tab=overview`, { replace: true });
          return;
        }

        const createPayload = {
          widget_linked_workspace: resolvedWorkspaceId,
          status: 'draft',
          widget_type: 'Web Widget',
          widget_language: 'pl-pl',
          widget_color: '#10b981',
          widget_position: 'right',
          auto_detect_domains: true,
          extract_page_context: true
        };

        const createResponse = await axios.post(
          `${DIRECTUS_INSTANCE}/items/widget_config`,
          createPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const createdWidgetId = createResponse.data?.data?.id;
        if (createdWidgetId) {
          navigate(`${aiAgentsBasePath}/${createdWidgetId}?tab=settings`, { replace: true });
          return;
        }

        if (!cancelled) {
          setCheckingExistingWidget(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to create/open widget:', error);
          setCheckingExistingWidget(false);
        }
      }
    };

    ensureWidgetExistsAndOpen();

    return () => {
      cancelled = true;
    };
  }, [aiAgentsBasePath, navigate, workspaceId]);

  const handleSuccess = () => {
    navigate(`${aiAgentsBasePath}?tab=overview`);
  };

  const handleCancel = () => {
    navigate(`${aiAgentsBasePath}?tab=overview`);
  };

  if (checkingExistingWidget) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-transparent max-w-7xl mx-auto">
      <WidgetConfigForm
        workspaceId={workspaceId}
        configId={null}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default WidgetDetails;
export {
  CreateWidgetForm
};
